'use client';

import { Lesson, FAQ, Membership, MarketplaceTag, Category, Aggregate } from '@shared/types';
import {
    PublicCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { FunctionComponent, useMemo } from 'react';
import DescriptionSection from '../../../pages_components/[educator_param]/[course_param]/DescriptionSection';
import EducatorSection from '../../../pages_components/[educator_param]/[course_param]/EducatorSection';
import FAQSection from '../../../pages_components/[educator_param]/[course_param]/FAQSection';
import HighlightsSection from '../../../pages_components/[educator_param]/[course_param]/HighlightsSection';
import ImagesSection from '../../../pages_components/[educator_param]/[course_param]/ImagesSection';
import LocationSection from '../../../pages_components/[educator_param]/[course_param]/LocationSection';
import styles from '../../../pages_components/[educator_param]/[course_param]/styles.module.scss';
import getCourseSchema from '../../../pages_components/[educator_param]/[course_param]/getCourseSchema';
import CourseContentSection from '../../../pages_components/[educator_param]/[course_param]/CourseContentSection';
import { CourseCarousel } from '../../../components/CourseCarousel';
import Container from '../../../components/Container';
import ReviewsSection from '../../../pages_components/[educator_param]/[course_param]/ReviewsSection';
import CategoryAndTagSection from '../../../pages_components/[educator_param]/[course_param]/CategoryAndTagSection';
import { getEducatorProfileLinks } from '../../../hooks/useEducatorProfileLinks/getEducatorProfileLinks';
import { ViewInAdminBanner } from '../../../components/ViewInAdminBanner';
import { AppContexts } from '../../../contexts/AppContext';
import { EducatorHeaderCard } from './EducatorHeaderCard';
import { MarketplaceOnly } from '../../../components/AppContext/MarketplaceOnly';
import { CoursePageBreadcrumbs } from './Breadcrumbs';
import { OccurrenceSectionWithoutExpiredOffers } from './OccurrenceSectionWithoutExpiredOffers';
import { BookAsideWithoutExpiredOffers } from './BookAsideWithoutExpiredOffers';

type CourseProps = {
    course: PublicCourse;
    courseDescription: string | null;
    educator: PublicEducator;
    lessons: Array<Lesson>;
    occurrences: Array<PublicOccurrence>;
    faqs: Array<FAQ>;
    memberships: Array<Membership>;
    paypal_installed: boolean;
    stripe_installed: boolean;
    vouchers_app_installed: boolean;
    marketplace_tags: Array<MarketplaceTag>;
    primary_category: Category | null;
    categories: Array<Category>;
    reviews_aggregate?: Aggregate;
    appContext: AppContexts['context'];
    showBreadcrumbs?: boolean;
};

export const Course: FunctionComponent<CourseProps> = ({
    course,
    courseDescription,
    educator,
    faqs,
    occurrences,
    lessons,
    memberships,
    vouchers_app_installed,
    marketplace_tags,
    primary_category,
    categories,
    reviews_aggregate,
    appContext,
    showBreadcrumbs = true,
}) => {
    const { coursesLink } = getEducatorProfileLinks(appContext, educator.slug);
    const isMarketplace = useMemo(() => appContext === 'marketplace', [appContext]);
    const isEmbed = useMemo(() => appContext === 'embed', [appContext]);

    return (
        <Container>
            <ViewInAdminBanner
                educatorId={educator._id}
                href={`${process.env.NEXT_PUBLIC_ADMIN_URL}/courses/${course._id}?educator-context=${educator._id}`}
            />
            {isMarketplace ? (
                <script
                    id="course-schema"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(
                            getCourseSchema(course, educator, reviews_aggregate, occurrences)
                        ),
                    }}
                />
            ) : null}
            {showBreadcrumbs && (
                <CoursePageBreadcrumbs
                    address={'address' in course && course.address ? course.address : null}
                    primaryCategory={primary_category}
                />
            )}
            <main
                key={course._id}
                className={styles.course_article}
                tabIndex={0}
                aria-labelledby="course-article-header"
            >
                <ImagesSection course={course} />
                <header>
                    <h1 id="course-article-header">{course.name}</h1>
                </header>
                <MarketplaceOnly>
                    <EducatorHeaderCard
                        name={educator.name}
                        slug={educator.slug}
                        overall_review_aggregate={educator.review_aggregate}
                        className={styles.educatorHeaderCard}
                    />
                </MarketplaceOnly>
                <BookAsideWithoutExpiredOffers
                    course={course}
                    educator={educator}
                    occurrences={occurrences}
                    memberships={memberships}
                    vouchersAppInstalled={vouchers_app_installed}
                    className={styles.bookAside}
                />
                <HighlightsSection
                    course={course}
                    occurrences={occurrences.length ? occurrences : null}
                    className={styles.courseHighlights}
                />
                <DescriptionSection description={courseDescription} />
                <FAQSection course={course} faqs={faqs} />
                <CourseContentSection course={course} lessons={lessons} />
                <OccurrenceSectionWithoutExpiredOffers
                    course={course}
                    educator={educator}
                    occurrences={occurrences}
                    className={styles.courseOccurrences}
                />
                <LocationSection course={course} />
                <MarketplaceOnly>
                    <EducatorSection educator={educator} />
                </MarketplaceOnly>
                <CategoryAndTagSection categories={categories} tags={marketplace_tags} />
                <ReviewsSection aggregate={reviews_aggregate} educator={educator} course={course} />
            </main>
            <aside className={styles.carousels_wrapper}>
                {isEmbed === false ? (
                    <div>
                        <CourseCarousel
                            heading={`More from ${educator.name}`}
                            filters={{ educator_ref: [educator._id] }}
                            endCardText={`View all listings by ${educator.name}`}
                            endCardLink={coursesLink}
                            name={`More listings from ${educator.name}`}
                            vatSettings={educator.vat_settings}
                        />
                    </div>
                ) : null}

                {isMarketplace && primary_category ? (
                    <div>
                        <CourseCarousel
                            heading={`More in ${primary_category.name}`}
                            filters={{ category_slug: [primary_category.slug] }}
                            endCardText={`View more ${primary_category.name} courses`}
                            endCardLink={`/courses/${primary_category.slug}`}
                            name={`More ${primary_category.name} courses`}
                            vatSettings={educator.vat_settings}
                        />
                    </div>
                ) : null}
            </aside>
        </Container>
    );
};
